import React from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Person, Work } from 'styled-icons/material';
import { University, FilePdf } from 'styled-icons/fa-solid';

import { useStore } from '@src/models';
import { candidateTheme } from 'src/theme';
import { LayoutBox, Flex, Text, Circle, Card, BgImage, Box } from 'src/kit';
import { Loader } from 'src/components';
import {
  StyledStepsContainer,
  StepButton,
  ApplicationWizardForm
} from '@src/features/candidates/features/applications';
import { EmployerNav, EmployerFooter } from './organisms';

const JobApplication = observer(() => {
  const { routerStore, resourcesStore, userStore, jobsStore } = useStore();
  const [stepNumber, setStepNumber] = React.useState(0);
  const { jobId } = useParams<{ jobId: string }>();

  const job = jobsStore.getJobById(jobId) || { allow_external_url: false, external_url: false };
  const isFetching = userStore.isFetching || resourcesStore.isFetching || jobsStore.isFetching;

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!isFetching && job.allow_external_url && job.external_url && userStore.isCandidate) {
      window.location.href = job.external_url;
    }
  }, [job.external_url, userStore.isCandidate, isFetching]);

  if (isFetching) {
    return <Loader css={{ height: '200px' }} />;
  }

  return (
    <ThemeProvider theme={candidateTheme}>
      <Box>
        <EmployerNav />
        <LayoutBox position="relative" height={380} mt="64px">
          <BgImage src={job?.company?.cover_picture_url} fullParentSize filter="brightness(0.8)" />
          <Flex position="relative" py={52} flexDirection="column" alignItems="center" justifyContent="center">
            <Circle as="img" src={job?.company?.logo_url} size={160} />
            <Text mt={25} color="white" fontSize={18} fontWeight="light" fontStyle="italic" textAlign="center">
              {job?.company?.motto}
            </Text>
          </Flex>
        </LayoutBox>
        <Card
          position="relative"
          m="-86px auto 50px"
          py={40}
          px={{ xs: 10, sm: 20, md: 30, lg: 40 }}
          width={1}
          maxWidth={840}
          bg="white"
          boxShadow="shadow1"
        >
          <StyledStepsContainer mb={40}>
            {getSteps(job).map((link, index) => (
              <StepButton
                key={index}
                onClick={() => setStepNumber(index)}
                className={getStepButtonClassName(index, stepNumber)}
              >
                <LayoutBox display={{ xs: 'none', md: 'block' }}>{`${index + 1}. ${link.label}`}</LayoutBox>
                <LayoutBox display={{ xs: 'block', md: 'none' }}>{link.icon}</LayoutBox>
              </StepButton>
            ))}
          </StyledStepsContainer>
          <ApplicationWizardForm
            {...{ routerStore, userStore, resourcesStore, jobsStore, stepNumber, setStepNumber }}
          />
        </Card>
        <EmployerFooter />
      </Box>
    </ThemeProvider>
  );
});

const getSteps = job => {
  const application_form_settings = job.application_form_settings;
  return [
    {
      label: 'Personal',
      icon: <Person size={22} />
    },
    application_form_settings?.professional_experience &&
      job.skipExternalUrl && {
        label: 'Employment history',
        icon: <Work size={22} />
      },
    application_form_settings?.education &&
      job.skipExternalUrl && {
        label: 'Education',
        icon: <University size={22} />
      },
    job.skipExternalUrl && {
      label: 'Others',
      icon: <FilePdf size={22} />
    }
  ].filter(Boolean);
};
const getStepButtonClassName = (index, stepNumber) => {
  if (index < stepNumber) return 'completed';
  if (index > stepNumber) return 'disabled';
  if (index === stepNumber) return 'active';
};

export default JobApplication;
